import UnsplashProvider from './providers/UnsplashProvider';
import PexelsProvider from './providers/PexelsProvider';
import PixabayProvider from './providers/PixabayProvider';
import FreesoundProvider from './providers/FreesoundProvider';
import TenorProvider from './providers/TenorProvider';
import GiphyProvider from './providers/GiphyProvider';
import OpenverseProvider from './providers/OpenverseProvider';

/**
 * Stock Media Service Manager
 * Manages multiple stock media providers and aggregates results
 * 
 * Usage:
 * const stockService = new StockMediaService();
 * const results = await stockService.search({ query: 'nature', mediaType: 'image' });
 */
class StockMediaService {
  constructor() {
    this.providers = [];
    this.initializeProviders();
  }

  /**
   * Initialize all available providers
   */
  initializeProviders() {
    // Initialize providers (only if API keys are available)
    const unsplash = new UnsplashProvider();
    const pexels = new PexelsProvider();
    const pixabay = new PixabayProvider();
    const freesound = new FreesoundProvider();
    const tenor = new TenorProvider();
    const giphy = new GiphyProvider();
    const openverse = new OpenverseProvider();

    // Add providers that have API keys configured
    if (unsplash.apiKey) this.providers.push(unsplash);
    if (pexels.apiKey) this.providers.push(pexels);
    if (pixabay.apiKey) this.providers.push(pixabay);
    if (freesound.apiKey) this.providers.push(freesound);
    if (tenor.apiKey) this.providers.push(tenor);
    if (giphy.apiKey) this.providers.push(giphy);
    
    // Openverse works without API key (with lower rate limits)
    // Always add it as it provides CC licensed content
    this.providers.push(openverse);

    console.log(`Stock Media Service initialized with ${this.providers.length} provider(s):`,
      this.providers.map(p => p.name).join(', '));
  }

  /**
   * Get all active providers
   */
  getProviders() {
    return this.providers.map(p => p.getInfo());
  }

  /**
   * Get providers that support a specific media type
   */
  getProvidersByMediaType(mediaType) {
    return this.providers.filter(p => p.supportsMediaType(mediaType));
  }

  /**
   * Search across all providers
   * @param {Object} params - Search parameters
   * @param {string} params.query - Search query
   * @param {string} params.mediaType - Type of media (image, video, audio)
   * @param {number} params.page - Page number
   * @param {number} params.perPage - Items per page
   * @param {string} params.provider - Optional: specific provider to use
   * @returns {Promise<Object>} - Aggregated results
   */
  async search({ query, mediaType = 'image', page = 1, perPage = 20, provider = null }) {
    // If specific provider requested, use only that one
    if (provider) {
      const selectedProvider = this.providers.find(p => p.name.toLowerCase() === provider.toLowerCase());
      if (selectedProvider && selectedProvider.supportsMediaType(mediaType)) {
        return await selectedProvider.search({ query, mediaType, page, perPage });
      }
      console.warn(`Provider ${provider} not found or doesn't support ${mediaType}`);
      return { data: [], hasMore: false, total: 0 };
    }

    // Get all providers that support this media type
    const supportedProviders = this.getProvidersByMediaType(mediaType);
    
    if (supportedProviders.length === 0) {
      console.warn(`No providers available for ${mediaType}`);
      return { data: [], hasMore: false, total: 0 };
    }

    // Search across all supported providers in parallel
    const results = await Promise.allSettled(
      supportedProviders.map(p => 
        p.search({ query, mediaType, page, perPage: Math.ceil(perPage / supportedProviders.length) })
      )
    );

    // Aggregate results
    const aggregatedData = [];
    let totalResults = 0;
    let hasMore = false;

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value.data) {
        aggregatedData.push(...result.value.data);
        totalResults += result.value.total || 0;
        hasMore = hasMore || result.value.hasMore;
      } else {
        console.error(`Provider ${supportedProviders[index].name} search failed:`, result.reason);
      }
    });

    // Shuffle results to mix providers (only on first page for consistency)
    const shuffledData = page === 1 ? this.shuffleArray(aggregatedData) : aggregatedData;
    
    // Take only the requested amount
    const finalData = shuffledData.slice(0, perPage);

    return {
      data: finalData,
      hasMore: hasMore,
      total: totalResults,
      page: page,
      providers: supportedProviders.map(p => p.name),
    };
  }

  /**
   * Get popular/trending media across all providers
   */
  async getPopular({ mediaType = 'image', page = 1, perPage = 20, provider = null }) {
    // If specific provider requested, use only that one
    if (provider) {
      const selectedProvider = this.providers.find(p => p.name.toLowerCase() === provider.toLowerCase());
      if (selectedProvider && selectedProvider.supportsMediaType(mediaType)) {
        return await selectedProvider.getPopular({ mediaType, page, perPage });
      }
      console.warn(`Provider ${provider} not found or doesn't support ${mediaType}`);
      return { data: [], hasMore: false, total: 0 };
    }

    // Get all providers that support this media type
    const supportedProviders = this.getProvidersByMediaType(mediaType);
    
    if (supportedProviders.length === 0) {
      console.warn(`No providers available for ${mediaType}`);
      return { data: [], hasMore: false, total: 0 };
    }

    // Get popular from all supported providers in parallel
    const results = await Promise.allSettled(
      supportedProviders.map(p => 
        p.getPopular({ mediaType, page, perPage: Math.ceil(perPage / supportedProviders.length) })
      )
    );

    // Aggregate results
    const aggregatedData = [];
    let hasMore = false;

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value.data) {
        aggregatedData.push(...result.value.data);
        hasMore = hasMore || result.value.hasMore;
      } else {
        console.error(`Provider ${supportedProviders[index].name} getPopular failed:`, result.reason);
      }
    });

    // Shuffle results to mix providers (only on first page for consistency)
    const shuffledData = page === 1 ? this.shuffleArray(aggregatedData) : aggregatedData;
    
    // Take only the requested amount
    const finalData = shuffledData.slice(0, perPage);

    return {
      data: finalData,
      hasMore: hasMore,
      total: null,
      page: page,
      providers: supportedProviders.map(p => p.name),
    };
  }

  /**
   * Trigger download event for a specific provider (e.g., Unsplash requires this)
   */
  async triggerDownload(item) {
    if (!item.provider) return;

    const provider = this.providers.find(p => p.name.toLowerCase() === item.provider.toLowerCase());
    if (provider && typeof provider.triggerDownload === 'function') {
      await provider.triggerDownload(item.downloadLocation);
    }
  }

  /**
   * Get attribution text for an item
   */
  getAttributionText(item) {
    if (item.attribution) return item.attribution;

    const provider = this.providers.find(p => p.name.toLowerCase() === item.provider?.toLowerCase());
    if (provider && typeof provider.getAttributionText === 'function') {
      return provider.getAttributionText(item, item.type);
    }

    return `${item.type === 'video' ? 'Video' : 'Photo'} by ${item.author?.name || 'Unknown'}`;
  }

  /**
   * Shuffle array (Fisher-Yates algorithm)
   */
  shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Check if service has any providers configured
   */
  hasProviders() {
    return this.providers.length > 0;
  }

  /**
   * Get provider by name
   */
  getProvider(name) {
    return this.providers.find(p => p.name.toLowerCase() === name.toLowerCase());
  }
}

// Export singleton instance
export default new StockMediaService();